import { useCallback, useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import type { Engine, ISourceOptions } from 'tsparticles-engine';

const ParticleBackground = () => {
  const theme = useTheme();
  const [isInitialized, setIsInitialized] = useState(false);

  // Determine primary and secondary colors based on theme
  const primaryColor = theme.palette.primary.main;
  const secondaryColor = theme.palette.secondary.main;
  const accentColor = theme.palette.mode === 'dark' ? '#90caf9' : '#1769aa';
  const tertiaryColor = theme.palette.mode === 'dark' ? '#f48fb1' : '#f50057';
  
  // Initialize tsParticles
  const particlesInit = useCallback(async (engine: Engine) => {
    await loadSlim(engine);
    setIsInitialized(true);
  }, []);

  // Apply animation based on theme mode
  useEffect(() => {
    if (isInitialized) {
      // If we need to refresh the component when theme changes
      setIsInitialized(false);
      setTimeout(() => setIsInitialized(true), 100);
    }
  }, [theme.palette.mode]);

  // Configuration for the particles - simplified tech-inspired animation
  const particlesConfig: ISourceOptions = {
    fullScreen: true, // Make it full width
    background: {
      color: {
        value: 'transparent',
      },
    },
    fpsLimit: 60,
    interactivity: {
      events: {
        onClick: {
          enable: false, // Disable click interactions
        },
        onHover: {
          enable: true,
          mode: 'repulse',
          parallax: {
            enable: false,
            force: 60,
            smooth: 10,
          },
        },
        resize: true,
      },
      modes: {
        repulse : {
          distance: 90,
          duration: 0.2,
        },
      },
    },
    particles: {
      color: {
        value: [primaryColor, secondaryColor, accentColor, tertiaryColor, '#00ffff'],
      },
      links: {
        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)',
        distance: 150,
        enable: true,
        opacity: 0.3,
        width: 1,
      },
      collisions: {
        enable: true,
      },
      move: {
        direction: 'none',
        enable: true,
        outModes: {
          default: 'bounce',
        },
        random: false,
        speed: 1.2, // Slower, more subtle movement
        straight: false,
      },
      number: {
        density: {
          enable: true,
          area: 1000,
        },
        value: 80,
      },
      opacity: {
        value: { min: 0.2, max: 0.5 }, // More transparent
        animation: {
          enable: true,
          speed: 0.5,
          minimumValue: 0.1,
          sync: false,
        },
      },
      shape: {
        type: ['circle', 'triangle'], // Removed stars, simplified shapes
      },
      size: {
        value: { min: 1, max: 4 }, // Smaller particles
        animation: {
          enable: true,
          speed: 1.5,
          minimumValue: 0.5,
          sync: false,
        },
      },
    },
    detectRetina: true,
  };

  return (
    <Particles
      id="tsparticles"
      options={particlesConfig}
      init={particlesInit}
    />
  );
};

export default ParticleBackground;
